// /switch — the public "Switching to Vidyapeeth360" page (MARKETING_10X_PLAN Phase-3 §3).
// The switching product is REAL and this page only sells what ships: the student CSV/XLSX
// Migration Copilot (mapping review → transactional commit), tenant quiet mode for non-essential
// outbound communication, invoice-bound gateway evidence, and the 90-day full-term trial.
// The evaluation matrix names the questions a school should verify and the exact evidence available
// in Vidyapeeth360. It does not make unverifiable claims about unnamed competitors.
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { m, useReducedMotion } from "framer-motion";
import {
  ArrowRight, Check, FileSpreadsheet, ShieldCheck, Landmark, MessagesSquare, Database,
  ClipboardCheck, Users, Rocket, RefreshCcw,
} from "lucide-react";
import { CTA_GRADIENT, ContactSalesModal, MarketingTopBar, SiteFooter } from "../components/marketing/shared";
import { MarketingMotion } from "../components/marketing/MarketingMotion";
import MarketingSeo from "../components/marketing/MarketingSeo";
import BrandWordmarkText from "../components/BrandWordmarkText";
import { Button } from "../components/ui/primitives";
import { init as initAnalytics, track } from "../lib/analytics";
import { CTA_PRIMARY_LABEL } from "../components/marketing/navigation";

const EASE = [0.22, 1, 0.36, 1];

// The five-step switch — each step is a shipped product surface, not a service promise.
const STEPS = [
  {
    icon: Users, title: "Register your workspace",
    desc: "Pick your School Code, board and academic year. Self-serve, no card required.",
  },
  {
    icon: FileSpreadsheet, title: "Import with the Migration Copilot",
    desc: "Upload a student CSV or XLSX file. The mapping helper proposes columns and flags row-level validation issues for review.",
  },
  {
    icon: ClipboardCheck, title: "Review before anything commits",
    desc: "You approve the mapping and preview the staged data. Only then does it commit — in one transaction, with a full audit trail.",
  },
  {
    icon: MessagesSquare, title: "Verify in quiet mode",
    desc: "New workspaces hold non-essential outbound communication while you explore, correct and train. Security OTPs and authorised staff invitations remain available.",
  },
  {
    icon: Rocket, title: "Go live on your date",
    desc: "Flip go-live when you're ready. Configured reminders and parent communication can begin from that point; imported student records remain under onboarding safeguards.",
  },
];

const EVALUATION_ROWS = [
  [
    "Student-record integrity",
    "Ask how mappings, rejected rows, duplicate checks and rollback are handled before a bulk import commits.",
    "CSV/XLSX mapping preview, row-level validation, review gate and one transactional commit.",
  ],
  [
    "Payment evidence",
    "Trace how a provider event reaches an invoice, then how final bank credit and deductions are reconciled.",
    "Provider callbacks update invoice evidence; final settlement remains a separate reconciliation step.",
  ],
  [
    "Onboarding communication",
    "Identify which messages are held during migration and which operational exceptions remain available.",
    "Quiet mode holds non-essential outbound communication; security OTPs and authorised staff invitations remain available.",
  ],
  [
    "Academic publishing",
    "Verify grading configuration, mark limits, publish locks, report-card templates and correction handling.",
    "Board-configured grading, server-side mark limits and locks after result publication; templates are reviewed during fit assessment.",
  ],
  [
    "Consent and retention",
    "Review purpose, consent, retention, access, export and erasure workflows alongside the school's legal responsibilities.",
    "Product controls support the school's DPDP programme; they are not legal certification or a compliance guarantee.",
  ],
  [
    "AI boundaries",
    "Ask which sources, permissions, configured safeguards and human-confirmation gates apply to each AI surface.",
    "Permission-scoped sources, configured identifier safeguards and confirmation gates for supported sensitive actions.",
  ],
];

// The hero's product proof: a token-accurate, ANIMATED recreation of the Migration Copilot's
// review step (I-05 — upload → proposed mapping → per-row validation → commit gate → quiet mode).
// One orchestrated entrance (staggered variants, house ease), no loops beyond a gentle scan sheen;
// fully gated by reduced-motion/pause. Data is SYNTHETIC and labeled — the mechanism is real.
const COPILOT_ROWS = [
  { name: "Aarav Sharma", cls: "6-A", fee: "₹42,000", ok: true },
  { name: "Meera Pillai", cls: "3-B", fee: "₹38,500", ok: true },
  { name: "R. Iyer", cls: "9-C", fee: "₹51,000", ok: false, flag: "DOB format — fix suggested" },
];
const copilotItem = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

function CopilotPreview({ reduce, testId = "copilot-preview" }) {
  return (
    <figure className="relative" data-testid={testId}>
      <div className="absolute -inset-6 rounded-[2rem] bg-primary/10 blur-3xl pointer-events-none" aria-hidden="true" />
      <m.div
        role="img"
        aria-label="Preview of the Migration Copilot review step: an uploaded spreadsheet's columns mapped to student fields, each row validated, one row flagged for correction, with quiet mode on and the commit step waiting for approval. Sample data."
        className="relative rounded-2xl border border-line bg-surface shadow-elevated overflow-hidden"
        variants={{ hidden: {}, show: { transition: { staggerChildren: reduce ? 0 : 0.14, delayChildren: reduce ? 0 : 0.15 } } }}
        initial={reduce ? false : "hidden"}
        whileInView="show"
        viewport={{ once: true, amount: 0.4 }}
      >
        <div aria-hidden="true">
          {/* header */}
          <m.div variants={copilotItem} className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <span className="inline-flex items-center gap-2 min-w-0">
              <span className="w-7 h-7 rounded-lg bg-primary/10 grid place-items-center shrink-0"><FileSpreadsheet className="w-4 h-4 text-primary" /></span>
              <span className="font-heading font-semibold text-ink text-[13.5px] truncate">Migration Copilot review</span>
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-soft border border-orange-border/60 px-2.5 py-1 text-[10.5px] font-bold text-orange-dark whitespace-nowrap">
              <span className="w-1.5 h-1.5 rounded-full bg-orange" /> QUIET MODE ON
            </span>
          </m.div>
          {/* proposed mapping */}
          <m.div variants={copilotItem} className="px-4 pt-3.5">
            <p className="text-[10.5px] font-bold uppercase tracking-wide text-ink-subtle">students.xlsx → proposed mapping</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {[["Student Name", "full_name"], ["Std/Sec", "class · section"], ["Fees Due", "invoice (₹ paise)"]].map(([from, to]) => (
                <span key={from} className="inline-flex items-center gap-1 rounded-lg border border-line bg-muted/60 px-2 py-1 text-[11px] font-medium text-ink-2">
                  {from} <ArrowRight className="w-3 h-3 text-primary" /> <span className="text-ink font-semibold">{to}</span>
                </span>
              ))}
            </div>
          </m.div>
          {/* validated rows */}
          <div className="px-4 py-3.5 space-y-2">
            {COPILOT_ROWS.map((r) => (
              <m.div key={r.name} variants={copilotItem}
                className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${r.ok ? "border-line bg-canvas" : "border-caution/40 bg-caution-soft/40"}`}>
                {r.ok
                  ? <Check className="w-3.5 h-3.5 text-positive shrink-0" strokeWidth={3} />
                  : <span className="w-3.5 h-3.5 rounded-full border-2 border-caution shrink-0" />}
                <span className="text-[12.5px] font-medium text-ink min-w-0 truncate">{r.name}</span>
                <span className="text-[11.5px] text-ink-subtle">{r.cls}</span>
                <span className="ml-auto text-[11.5px] tabular-nums text-ink-2">{r.fee}</span>
                {!r.ok && <span className="text-[10.5px] font-semibold text-caution whitespace-nowrap">{r.flag}</span>}
              </m.div>
            ))}
          </div>
          {/* commit gate */}
          <m.div variants={copilotItem} className="flex items-center justify-between gap-3 border-t border-line bg-muted/40 px-4 py-3">
            <p className="text-[11.5px] text-ink-2"><span className="font-semibold text-ink tabular-nums">412 of 413</span> rows valid — 1 to fix</p>
            <span className={`inline-flex items-center gap-1.5 rounded-full ${CTA_GRADIENT} px-3.5 py-1.5 text-[11.5px] font-semibold text-white shadow-[0_6px_16px_rgba(29,78,216,0.25)]`}>
              Commit after review <ArrowRight className="w-3 h-3" />
            </span>
          </m.div>
          {/* one quiet scan sheen after the entrance — never a busy loop */}
          {!reduce && (
            <m.div className="absolute inset-0 pointer-events-none"
              style={{ background: "linear-gradient(105deg, transparent 42%, rgba(37,99,235,0.06) 50%, transparent 58%)", backgroundSize: "260% 100%" }}
              initial={{ backgroundPosition: "150% 0%" }}
              whileInView={{ backgroundPosition: "-150% 0%" }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 2.2, ease: "easeInOut", delay: 1.1 }} />
          )}
        </div>
      </m.div>
      <figcaption className="mt-2.5 text-[11.5px] text-ink-subtle text-center">Illustrative preview — sample data. The review, validation, and quiet-mode mechanics are the shipped product.</figcaption>
    </figure>
  );
}

export default function SwitchPage() {
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const [salesOpen, setSalesOpen] = useState(false);

  useEffect(() => {
    initAnalytics();
    window.scrollTo(0, 0);
  }, []);

  const reveal = (delay = 0) => ({
    initial: reduce ? false : { opacity: 0, y: 24 },
    whileInView: reduce ? undefined : { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.25 },
    transition: { duration: 0.55, ease: EASE, delay: reduce ? 0 : delay },
  });

  return (
    <MarketingMotion>
    <div className="min-h-screen bg-canvas text-ink flex flex-col landing-page" data-testid="switch-page">
      <MarketingSeo
        title="Switch Your School ERP — Reviewed Migration | Vidyapeeth360"
        description="Switching school ERP or leaving spreadsheets? Review every imported student before the import commits, and hold routine parent messages until you go live."
        path="/switch"
      />
      <MarketingTopBar onBookDemo={() => setSalesOpen(true)} />
      <main className="flex-1">
        {/* Header — the switching promise, stated plainly, with the Copilot review as its proof. */}
        <section className="mx-auto max-w-6xl px-5 pt-12 sm:pt-16 pb-10 grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,440px)] lg:items-center">
          <div className="max-w-3xl">
            <p className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3.5 py-1.5 text-[13px] font-semibold text-ink-2">
              <RefreshCcw className="w-4 h-4 text-primary" aria-hidden="true" /> Switching &amp; migration
            </p>
            {/* POS-5/PGC-8 (2026-08-18): lead with the fear the page solves, benefit-first —
                the precise quiet-mode scope (non-essential + named exceptions) stays in the
                subhead directly below. */}
            <h1 className="mt-4 font-heading font-bold text-3xl sm:text-[40px] leading-[1.12] text-ink tracking-tight">
              Switch school software. Parents hear nothing routine until you go live.
            </h1>
            <p className="mt-4 text-ink-2 text-[16px] leading-relaxed max-w-2xl">
              A responsible migration needs two safeguards: review imported records before commit,
              and prevent routine messages from leaving while the school verifies its new workspace.
              <BrandWordmarkText /> provides a student CSV/XLSX mapping review plus quiet mode for
              non-essential outbound communication until the school explicitly goes live.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button size="lg" onClick={() => { track("trial_cta_click", { placement: "switch_page" }); navigate("/register"); }} data-testid="switch-register">
                {CTA_PRIMARY_LABEL} <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => { track("demo_request", { placement: "switch_page" }); setSalesOpen(true); }} data-testid="switch-demo">
                Book a migration walkthrough
              </Button>
            </div>
            <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-ink-2">
              {[
                "Student CSV/XLSX mapping review",
                "Non-essential communication held in quiet mode",
                "90-day full-term trial — no card",
              ].map((t) => (
                <li key={t} className="inline-flex items-center gap-1.5">
                  <Check className="w-4 h-4 text-positive shrink-0" aria-hidden="true" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="hidden lg:block">
            <CopilotPreview reduce={reduce} />
          </div>
          {/* PGC-5 — the page's product proof must reach the mobile visitor too: a static
              (reduce) Copilot review renders below the hero copy on <lg instead of hiding. */}
          <div className="lg:hidden">
            <CopilotPreview reduce testId="copilot-preview-mobile" />
          </div>
        </section>

        {/* The switch, step by step — every step is a shipped surface. */}
        <section className="border-y border-line bg-surface" aria-labelledby="switch-steps-h">
          <div className="mx-auto max-w-6xl px-5 py-12 sm:py-16">
            <m.h2 id="switch-steps-h" {...reveal()} className="font-heading font-bold text-2xl sm:text-[28px] text-ink tracking-tight max-w-2xl">
              Five visible checkpoints from file to go-live.
            </m.h2>
            <ol className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-5 md:[&>li:last-child]:col-span-2 lg:[&>li:last-child]:col-span-1">
              {STEPS.map((s, i) => (
                <m.li key={s.title} {...reveal(Math.min(i * 0.06, 0.3))}
                  className="relative rounded-2xl border border-line bg-canvas p-5">
                  <span className="flex items-center justify-between">
                    <span className="w-10 h-10 rounded-xl bg-primary/10 grid place-items-center">
                      <s.icon className="w-5 h-5 text-primary" aria-hidden="true" />
                    </span>
                    <span className="font-heading font-bold text-ink-faint text-lg tabular-nums" aria-hidden="true">{i + 1}</span>
                  </span>
                  <h3 className="mt-3.5 font-heading font-semibold text-ink text-[15px] leading-snug">{s.title}</h3>
                  <p className="mt-1.5 text-[13px] text-ink-2 leading-relaxed">{s.desc}</p>
                </m.li>
              ))}
            </ol>
            <div className="mt-8 flex items-start gap-4 rounded-2xl border border-primary/20 bg-primary/[0.05] p-5 max-w-3xl">
              <span className="w-10 h-10 rounded-xl bg-primary/10 grid place-items-center shrink-0"><ShieldCheck className="w-5 h-5 text-primary" aria-hidden="true" /></span>
              <p className="text-sm text-ink-2 leading-relaxed">
                <span className="font-semibold text-ink">Onboarding communication safeguard:</span>{" "}
                quiet mode holds non-essential outbound communication while the school verifies its
                imported records. Security OTPs and authorised staff invitations remain operational
                exceptions; reminder and recovery eligibility should be confirmed in the migration walkthrough.
              </p>
            </div>
          </div>
        </section>

        {/* Evaluation matrix — exact questions and product evidence, without unnamed-vendor claims. */}
        <section className="mx-auto max-w-6xl px-5 py-12 sm:py-16" aria-labelledby="switch-compare-h">
          <m.div {...reveal()} className="max-w-2xl">
            <h2 id="switch-compare-h" className="font-heading font-bold text-2xl sm:text-[28px] text-ink tracking-tight">
              Compare migrations on evidence — what moves, what changes, and what your team must do.
            </h2>
            <p className="mt-3 text-ink-2 text-[15px] leading-relaxed">
              Bring this checklist to every vendor conversation. It separates a promising feature
              name from the controls, boundaries and hand-offs that make it safe to operate.
            </p>
          </m.div>
          <div tabIndex={0} className="mt-8 overflow-x-auto [contain:layout_inline-size] rounded-2xl border border-line focus-visible:outline-none focus-visible:ring-2 ring-primary" data-testid="switch-compare">
            <table className="w-full text-sm min-w-[760px]">
              <caption className="sr-only">Questions to evaluate during a school software migration and the corresponding Vidyapeeth360 evidence</caption>
              <thead>
                <tr className="bg-muted">
                  <th scope="col" className="text-left px-4 py-3 font-semibold text-ink w-[190px]">Decision area</th>
                  <th scope="col" className="text-left px-4 py-3 font-semibold text-ink-2 w-[300px]">What to verify</th>
                  {/* The evidence column stays pinned while the table scrolls on phones. */}
                  <th scope="col" className="px-4 py-3 text-left font-heading font-bold text-primary sticky right-0 bg-muted border-l border-line shadow-[-8px_0_12px_-10px_rgba(15,23,42,0.35)] lg:shadow-none w-[300px]"><BrandWordmarkText /> evidence</th>
                </tr>
              </thead>
              <tbody>
                {EVALUATION_ROWS.map(([area, question, evidence], i) => (
                  <tr key={area} className={i % 2 === 1 ? "border-t border-line bg-muted/40" : "border-t border-line"}>
                    <th scope="row" className="text-left align-top px-4 py-4 font-semibold text-ink-2">{area}</th>
                    <td className="px-4 py-4 align-top text-[13px] leading-relaxed text-ink-subtle">{question}</td>
                    <td className="px-4 py-4 align-top text-[13px] leading-relaxed text-ink-2 sticky right-0 bg-surface border-l border-line shadow-[-8px_0_12px_-10px_rgba(15,23,42,0.35)] lg:shadow-none">
                      <span className="inline-flex items-start gap-2">
                        <Check className="mt-0.5 w-4 h-4 text-positive shrink-0" aria-hidden="true" />
                        <span>{evidence}</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-[12.5px] text-ink-subtle sm:hidden" aria-hidden="true">Swipe the table to compare ↔</p>
          <p className="mt-4 text-[13px] text-ink-subtle max-w-3xl">
            Evidence describes the current product and its stated boundaries. Bring your source
            fields, providers, policies and required reports to a walkthrough for a fit review before rollout.
          </p>
        </section>

        {/* Objection rail — three product FACTS a switching evaluator needs. Deliberately not
            called "guarantees" (claims-registry S10: this site makes no promise it cannot evidence,
            and an accessible name is public copy too — screen-reader users read it verbatim). */}
        <section className="border-y border-line bg-surface" aria-labelledby="switch-evaluate-facts-h">
          <div className="mx-auto max-w-6xl px-5 py-12 sm:py-16">
            <h2 id="switch-evaluate-facts-h" className="sr-only">What stays true while you evaluate</h2>
            <div className="grid gap-5 md:grid-cols-3">
              {[
                [Landmark, "Invoice-bound payment evidence", "Fee collection runs through your school's connected gateway account; provider callbacks update the invoice, while your team reconciles final bank credit."],
                // TRUST-13: the explicit exit/walk-away statement, in the registry-approved
                // /pricing wording family (supported exports + documented erasure + no
                // auto-conversion) — a switching evaluator needs the way OUT stated here.
                [Database, "Data access and exit are documented", "Each school's records are walled off by database-enforced isolation. Use supported exports while access is active, request erasure through the documented process — and the 90-day trial never becomes paid on its own."],
                [ShieldCheck, "Routine messages stay quiet until go-live", "Quiet mode holds non-essential outbound communication. Security OTPs and authorised staff invitations remain operational exceptions."],
              ].map(([Icon, t, d], i) => (
                <m.div key={t} {...reveal(Math.min(i * 0.07, 0.21))} className="rounded-2xl border border-line bg-canvas p-6">
                  <span className="w-11 h-11 rounded-xl bg-primary/10 grid place-items-center">
                    <Icon className="w-5 h-5 text-primary" aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-heading font-semibold text-ink text-[16px] leading-snug">{t}</h3>
                  <p className="mt-2 text-[14px] text-ink-2 leading-relaxed">{d}</p>
                </m.div>
              ))}
            </div>
            <p className="mt-6 text-sm text-ink-2">
              The full security story — tenant isolation, AI privacy, consent and retention — is on the{" "}
              <Link to="/security" className="font-medium text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary">Security &amp; Compliance page</Link>.
            </p>
          </div>
        </section>

        {/* Close — one ask, stated as the outcome. */}
        <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <div className="rounded-3xl bg-gradient-to-br from-[var(--vp-close-band-from)] via-[var(--vp-close-band-via)] to-[var(--vp-close-band-to)] p-8 sm:p-12 text-center relative overflow-hidden">
            <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-orange/15 blur-3xl pointer-events-none" aria-hidden="true" />
            <h2 className="font-heading font-bold text-[26px] sm:text-[32px] text-white tracking-tight max-w-2xl mx-auto leading-tight">
              Bring a student CSV or XLSX. Review the move before commit.
            </h2>
            <p className="text-white/75 mt-3 max-w-xl mx-auto leading-relaxed">
              Register free, preview the student import, and use the current 90-day trial to evaluate the
              workflows you need while non-essential communication remains in quiet mode.
            </p>
            <div className="mt-7 flex flex-wrap gap-3 justify-center">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 shadow-lg" onClick={() => { track("trial_cta_click", { placement: "switch_page_close" }); navigate("/register"); }}>
                {CTA_PRIMARY_LABEL} <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Button>
              <Button size="lg" variant="ghost" className="text-white border border-white/40 hover:bg-white/10" onClick={() => { track("demo_request", { placement: "switch_page_close" }); setSalesOpen(true); }}>
                Book a migration walkthrough
              </Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <ContactSalesModal open={salesOpen} onClose={() => setSalesOpen(false)} contextLabel="migration, switching and implementation planning" placement="switch_page" />
    </div>
    </MarketingMotion>
  );
}
